/**
 * cs-video-popup.js
 * ---------------------------------------------------------------------------
 * Turns plain YouTube / Vimeo links inside rich-text content into lightbox
 * triggers. Built for rich text metafields (like "How To use"), which only
 * allow paragraphs, bold, italic, lists and links -- no HTML embeds. So the
 * merchant just links a piece of text to a video URL and this script opens it
 * in a popup instead of navigating away.
 *
 * Where it applies (SCOPES below):
 *   - .accordion__content          -> every collapsible block, product page included
 *   - .product-info__text .prose   -> plain text blocks on the product page
 *   - [data-video-popup]           -> opt-in, add this attribute to any wrapper
 *
 * Supported URLs: youtube.com/watch, youtu.be, /shorts, /embed, /live,
 * vimeo.com/ID and vimeo.com/ID/privatehash. Timestamps (?t= or ?start=) are
 * kept, in both "90" and "1m30s" form.
 *
 * The iframe is only created on click, never on page load, so no YouTube
 * payload is downloaded unless someone actually asks for the video. If this
 * script fails to load the links stay ordinary links and still work.
 */
(function () {
  'use strict';

  var SCOPES = '.accordion__content, .product-info__text .prose, [data-video-popup]';
  var LINK_CLASS = 'cs-vpop-link';

  /* ----------------------------------------------------------------------- *
   * URL parsing
   * ----------------------------------------------------------------------- */

  function readParam(url, name) {
    // "#" is included because Vimeo writes timestamps as "#t=45s".
    var match = url.match(new RegExp('[?&#]' + name + '=([^&#]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  /** Accepts "90" or "1h2m3s" and returns whole seconds. */
  function toSeconds(value) {
    if (!value) return 0;
    if (/^\d+$/.test(value)) return parseInt(value, 10);

    var parts = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
    if (!parts) return 0;

    return parseInt(parts[1] || 0, 10) * 3600 + parseInt(parts[2] || 0, 10) * 60 + parseInt(parts[3] || 0, 10);
  }

  /** Returns an embeddable src for a video URL, or null if it isn't one. */
  function parseVideo(url) {
    if (!url) return null;

    var start = toSeconds(readParam(url, 't') || readParam(url, 'start'));

    var youtube = url.match(
      /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
    );

    if (youtube) {
      // youtube-nocookie keeps the popup from setting tracking cookies before
      // the visitor has even pressed play.
      var src = 'https://www.youtube-nocookie.com/embed/' + youtube[1] + '?autoplay=1&rel=0&playsinline=1';
      return { src: start ? src + '&start=' + start : src };
    }

    var vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([A-Za-z0-9]+))?/);

    if (vimeo) {
      var vimeoSrc = 'https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1&title=0&byline=0&portrait=0';
      if (vimeo[2]) vimeoSrc += '&h=' + vimeo[2];
      return { src: start ? vimeoSrc + '#t=' + start + 's' : vimeoSrc };
    }

    return null;
  }

  /* ----------------------------------------------------------------------- *
   * The popup itself -- one instance, created on first use and reused
   * ----------------------------------------------------------------------- */

  var popup = null;
  var frame = null;
  var lastTrigger = null;

  function buildPopup() {
    var root = document.createElement('div');

    root.className = 'cs-vpop';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Video');
    root.hidden = true;

    root.innerHTML =
      '<div class="cs-vpop__overlay" data-cs-vpop-close></div>' +
      '<div class="cs-vpop__panel">' +
      '<button type="button" class="cs-vpop__close" data-cs-vpop-close aria-label="Close video">&times;</button>' +
      '<div class="cs-vpop__frame"></div>' +
      '</div>';

    document.body.appendChild(root);

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-cs-vpop-close]')) close();
    });

    popup = root;
    frame = root.querySelector('.cs-vpop__frame');

    return root;
  }

  function open(src, trigger) {
    if (!popup) buildPopup();

    lastTrigger = trigger || null;

    var iframe = document.createElement('iframe');

    iframe.setAttribute('src', src);
    iframe.setAttribute('title', 'Video');
    iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('frameborder', '0');

    frame.textContent = '';
    frame.appendChild(iframe);

    popup.hidden = false;
    document.documentElement.classList.add('cs-vpop-open');

    // One frame later so the opacity/scale transition actually runs.
    requestAnimationFrame(function () {
      popup.classList.add('is-open');
      popup.querySelector('.cs-vpop__close').focus();
    });
  }

  function close() {
    if (!popup || popup.hidden) return;

    popup.classList.remove('is-open');
    document.documentElement.classList.remove('cs-vpop-open');

    // Dropping the iframe is what stops playback -- pausing it isn't possible
    // without loading each provider's JS API.
    frame.textContent = '';
    popup.hidden = true;

    if (lastTrigger) {
      lastTrigger.focus();
      lastTrigger = null;
    }
  }

  /* ----------------------------------------------------------------------- *
   * Wiring
   * ----------------------------------------------------------------------- */

  // Delegated, so links added later (variant switches, theme editor reloads,
  // accordions rendered by a section) work without re-binding anything.
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    var link = event.target.closest('a[href]');
    if (!link || !link.closest(SCOPES)) return;

    var video = parseVideo(link.getAttribute('href'));
    if (!video) return;

    event.preventDefault();
    open(video.src, link);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') close();
  });

  /** Marks matching links so they get the play icon and pointer cursor. */
  function decorate(root) {
    var scopes = (root || document).querySelectorAll(SCOPES);

    for (var i = 0; i < scopes.length; i++) {
      var links = scopes[i].querySelectorAll('a[href]');

      for (var j = 0; j < links.length; j++) {
        if (links[j].classList.contains(LINK_CLASS)) continue;
        if (!parseVideo(links[j].getAttribute('href'))) continue;

        links[j].classList.add(LINK_CLASS);
        links[j].setAttribute('aria-haspopup', 'dialog');
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      decorate();
    });
  } else {
    decorate();
  }

  document.addEventListener('shopify:section:load', function (event) {
    decorate(event.target);
  });
})();
